import React, { useState } from 'react';
import Head from 'next/head';
import { Accordion, Tabs, Text } from '@belong/ui';
import { StringComponent } from 'src/features/faqs/components/string/String';
import { FAQS_STRINGS } from 'src/features/faqs/faqs.strings';

type Faq = {
  q: string;
  a: string[];
};

/** The homepage's curated picks out of the FAQ page's own two lists. */
const HOMEOWNER_FAQ_INDEXES = [4, 6, 9, 8, 2];
const RESIDENT_FAQ_INDEXES = [0, 1, 2, 3, 5];

// `!` on the element: these are fixed indexes into lists declared in this
// repo, not user input.
const pickFaqs = (faqs: Faq[], indexes: number[]) =>
  indexes.map((index) => faqs[index]!);

const faqTexts = {
  title: 'Frequently Asked Questions',
  activeTabs: ['For Homeowners', 'For Residents'],
  collapsable: {
    homeowner: pickFaqs(FAQS_STRINGS.homeowner, HOMEOWNER_FAQ_INDEXES),
    resident: pickFaqs(FAQS_STRINGS.resident, RESIDENT_FAQ_INDEXES),
  },
};

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    ...faqTexts.collapsable.homeowner,
    ...faqTexts.collapsable.resident,
  ].map((faq) => {
    return {
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a.join('\n'),
      },
    };
  }),
};

export const FaqSection = () => {
  const [selectedQuestion, setSelectedQuestion] = useState(-1);

  const createAccordionItems = (faqs: Faq[]) => {
    return faqs.map((faq, i) => ({
      title: <div id={`question-${i + 1}`}>{faq.q}</div>,
      content: <StringComponent string={faq.a} />,
    }));
  };

  const homeownerAccordionItems = createAccordionItems(
    faqTexts.collapsable.homeowner,
  );
  const residentAccordionItems = createAccordionItems(
    faqTexts.collapsable.resident,
  );

  const itemsToAccordion = [homeownerAccordionItems, residentAccordionItems];

  const handleClickAccordion =
    (
      currVal: number,
      updateFunc: React.Dispatch<React.SetStateAction<number>>,
    ) =>
    (selectedOption: number) => {
      const nextSelectedOption =
        selectedOption === currVal ? -1 : selectedOption;
      updateFunc(nextSelectedOption);
    };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqStructuredData),
          }}
        />
      </Head>

      <Text className="mb-xl text-center" variant="h2" fontWeight="semibold">
        {faqTexts.title}
      </Text>

      <Tabs defaultIndex={0} isDraggable>
        <Tabs.TabList className="mb-xl block text-center">
          {faqTexts.activeTabs.map((state, i) => (
            <Tabs.TabItem className="border-2" key={i} index={i}>
              {state}
            </Tabs.TabItem>
          ))}
        </Tabs.TabList>
        <Tabs.PanelList>
          {itemsToAccordion.map((items, i) => (
            <Tabs.PanelItem key={`accordion-${i + 1}`}>
              <Accordion
                items={items}
                // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
                index={selectedQuestion[i]}
                onChange={handleClickAccordion(
                  selectedQuestion,
                  setSelectedQuestion,
                )}
              ></Accordion>
            </Tabs.PanelItem>
          ))}
        </Tabs.PanelList>
      </Tabs>
    </>
  );
};
